import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowRight, ArrowUpRight, Minus } from "lucide-react";
import {
  api,
  KasiaSummaryCard,
  KasiaSummaryStatus,
  KasiaSummaryVerdict,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DailyTrendChart from "@/components/DailyTrendChart";
import { cn, formatPLN } from "@/lib/utils";

const PERIODS: { days: 7 | 14 | 30; label: string }[] = [
  { days: 7, label: "Tydzień" },
  { days: 14, label: "2 tygodnie" },
  { days: 30, label: "Miesiąc" },
];

function statusIcon(status: KasiaSummaryStatus) {
  if (status === "lepiej") return ArrowUpRight;
  if (status === "gorzej") return ArrowDownRight;
  if (status === "malo_danych") return Minus;
  return ArrowRight;
}

function statusColor(status: KasiaSummaryStatus): string {
  if (status === "lepiej") return "text-emerald-500";
  if (status === "gorzej") return "text-rose-500";
  if (status === "malo_danych") return "text-amber-500";
  return "text-muted-foreground";
}

function statusDot(status: KasiaSummaryStatus): string {
  if (status === "lepiej") return "bg-emerald-500";
  if (status === "gorzej") return "bg-rose-500";
  if (status === "malo_danych") return "bg-amber-500";
  return "bg-muted-foreground/60";
}

function formatDayPl(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

export default function Podsumowanie() {
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [openDay, setOpenDay] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["kasia-summary", days],
    queryFn: () => api.kasiaSummary(days),
    retry: false,
  });

  const data = query.data;
  const chartData = useMemo(
    () =>
      (data?.daily?.series ?? []).map((r) => ({
        date: r.date,
        revenue: r.revenue,
      })),
    [data?.daily?.series],
  );

  const best = data?.daily?.best_day;

  return (
    <div>
      <PageHeader
        title="Podsumowanie"
        subtitle="Prosto: ile zarobiłyśmy, czy jest lepiej, co robiłyśmy"
        actions={
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <Button
                key={p.days}
                size="sm"
                variant={days === p.days ? "default" : "outline"}
                onClick={() => setDays(p.days)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        }
      />

      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać podsumowania.
          </CardContent>
        </Card>
      )}

      {data && (
        <div className="grid gap-6">
          {data.period && (
            <p className="text-sm text-muted-foreground">
              {data.period.label_pl}: {formatDayPl(data.period.date_from)} -{" "}
              {formatDayPl(data.period.date_to)}
              {" · "}
              porównanie z: {data.period.prev_label_pl}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            {(data.cards ?? []).map((card, i) => (
              <SummaryCard key={card.id} card={card} index={i} />
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Co z tego wynika</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(data.verdicts ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Brak werdyktów.</p>
              ) : (
                (data.verdicts as KasiaSummaryVerdict[]).map((v, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className={cn(
                        "mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full",
                        statusDot(v.status),
                      )}
                    />
                    <p className="text-sm leading-relaxed text-foreground/90">{v.text}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Co robiłyśmy w tym czasie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(data.deeds?.chips ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {data.deeds?.empty_hint ||
                    "Brak wpisów. Dodawaj je w Sprzedaż → Zdarzenia."}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.deeds.chips.map((c) => (
                    <span
                      key={c.type}
                      className="rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-sm"
                    >
                      {c.count}× {c.label}
                    </span>
                  ))}
                </div>
              )}

              {(data.deeds?.by_day ?? []).length > 0 && (
                <div className="space-y-2 border-t border-border/50 pt-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Dzień po dniu
                  </p>
                  {data.deeds.by_day.slice(0, 10).map((d) => {
                    const open = openDay === d.date;
                    return (
                      <div key={d.date} className="rounded-md border border-border/60">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent/30"
                          onClick={() => setOpenDay(open ? null : d.date)}
                        >
                          <span className="font-medium">{formatDayPl(d.date)}</span>
                          <span className="text-xs text-muted-foreground">
                            {d.events.length} wpis{d.events.length === 1 ? "" : "ów"}
                          </span>
                        </button>
                        {open && (
                          <ul className="space-y-1 border-t border-border/50 px-3 py-2 text-xs text-muted-foreground">
                            {d.events.map((ev, idx) => (
                              <li key={idx}>
                                <span className="font-medium text-foreground/80">
                                  {ev.label}
                                  {ev.qty > 1 ? ` ×${ev.qty}` : ""}
                                </span>
                                {ev.title ? ` - ${ev.title}` : ""}
                                {ev.notes ? ` (${ev.notes})` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <DailyTrendChart
            title="Pieniądze dzień po dniu"
            subtitle={
              best
                ? `Najlepszy dzień: ${formatDayPl(best.date)} (${formatPLN(best.revenue)})`
                : "Przychód z zakupów w wybranym okresie"
            }
            data={chartData}
            series={[{ key: "revenue", label: "Przychód", format: "pln" }]}
            mode="bar"
            hideForSingleDay={false}
          />
        </div>
      )}
    </div>
  );
}

function SummaryCard({ card, index }: { card: KasiaSummaryCard; index: number }) {
  const Icon = statusIcon(card.status);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="h-full">
        <CardContent className="space-y-3 pt-6">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">{card.label}</p>
            <Icon className={cn("h-5 w-5", statusColor(card.status))} />
          </div>
          <p className="text-3xl font-bold tracking-tight">{card.value_display}</p>
          <p className={cn("text-sm", statusColor(card.status))}>{card.compare_text}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
